import React, { useMemo, useState } from "react";
import PageIntro from "../../../components/admin/PageIntro";
import SectionCard from "../../../components/admin/SectionCard";
import InfoCard from "../../../components/admin/InfoCard";
import { adminTheme } from "../../../components/admin/adminTheme";
import {
  createEmployee,
  HNB_STAFF_ROLE_VALUES,
  type HnbStaffRole,
} from "../api";

type FormState = {
  fullName: string;
  companyEmail: string;
  mobileNumber: string;
  role: HnbStaffRole;
  department: string;
  designation: string;
  reportingTo: string;
  assignedAreaCode: string;
  assignedDistrictCode: string;
  joiningDate: string;
  employmentType: string;
  notes: string;
};

type CreatedEmployeeState = {
  staffId: string;
  fullName: string;
  companyEmail: string;
  defaultPassword: string;
  mustChangePassword: boolean;
};

const roleOptions: HnbStaffRole[] = [...HNB_STAFF_ROLE_VALUES];

const inputStyle: React.CSSProperties = {
  width: "100%",
  padding: "10px 12px",
  borderRadius: adminTheme.radius.sm,
  border: `1px solid ${adminTheme.color.border}`,
  background: adminTheme.color.surface,
  color: adminTheme.color.text,
  font: "inherit",
};

const labelStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: "6px",
  color: adminTheme.color.text,
  fontWeight: 600,
};

const primaryButtonStyle: React.CSSProperties = {
  padding: "10px 14px",
  borderRadius: adminTheme.radius.sm,
  border: `1px solid ${adminTheme.color.text}`,
  background: adminTheme.color.text,
  color: adminTheme.color.pageBg,
  fontWeight: 700,
  cursor: "pointer",
};

const secondaryPanelStyle: React.CSSProperties = {
  border: `1px solid ${adminTheme.color.border}`,
  borderRadius: adminTheme.radius.sm,
  background: adminTheme.color.surface,
  padding: adminTheme.space.md,
};

const alertStyle: React.CSSProperties = {
  borderRadius: adminTheme.radius.sm,
  padding: adminTheme.space.md,
  border: `1px solid ${adminTheme.color.border}`,
  background: adminTheme.color.surface,
};

const initialFormState: FormState = {
  fullName: "",
  companyEmail: "",
  mobileNumber: "",
  role: "HR_MANAGER",
  department: "HR",
  designation: "",
  reportingTo: "",
  assignedAreaCode: "",
  assignedDistrictCode: "",
  joiningDate: "",
  employmentType: "Full-time",
  notes: "",
};

const HrEmployeeOnboardingPage: React.FC = () => {
  const [form, setForm] = useState<FormState>(initialFormState);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [createdEmployee, setCreatedEmployee] =
    useState<CreatedEmployeeState | null>(null);

  const defaultPassword = "hnb@12345";

  const generatedPreviewStaffId = useMemo(
    () => createdEmployee?.staffId || "Auto-generated on submit",
    [createdEmployee]
  );

  const handleChange =
    (field: keyof FormState) =>
    (
      event: React.ChangeEvent<
        HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
      >
    ) => {
      const value = event.target.value;

      setForm((current) => ({
        ...current,
        [field]: value,
      }));

      if (error) {
        setError(null);
      }
    };

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();

    if (isSubmitting) {
      return;
    }

    setIsSubmitting(true);
    setError(null);
    setCreatedEmployee(null);

    try {
      const employee = await createEmployee({
        fullName: form.fullName.trim(),
        companyEmail: form.companyEmail.trim().toLowerCase(),
        mobileNumber: form.mobileNumber.trim(),
        role: form.role,
        department: form.department.trim(),
        designation: form.designation.trim(),
        reportingTo: form.reportingTo.trim(),
        assignedAreaCode: form.assignedAreaCode.trim(),
        assignedDistrictCode: form.assignedDistrictCode.trim(),
        joiningDate: form.joiningDate,
        employmentType: form.employmentType.trim(),
        notes: form.notes.trim(),
      });

      setCreatedEmployee({
        staffId: employee.staffId,
        fullName: employee.fullName,
        companyEmail: employee.companyEmail,
        defaultPassword: employee.defaultPassword,
        mustChangePassword: employee.mustChangePassword,
      });

      setForm(initialFormState);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to create employee."
      );
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: adminTheme.space.section,
        padding: adminTheme.space.section,
        background: adminTheme.color.pageBg,
        minHeight: "100%",
      }}
    >
      <PageIntro
        title="Employee onboarding"
        message="Create new HnB employees, assign role and reporting structure, and issue default first-login credentials."
      />

      <SectionCard title="Login identity policy">
        <InfoCard
          title="First login rule"
          body={`Each new employee receives an auto-generated staff ID and the default password ${defaultPassword}. On first successful login, the user must immediately change the password before dashboard access is granted.`}
        />
      </SectionCard>

      <SectionCard title="Create employee">
        <form onSubmit={handleSubmit} noValidate>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
              gap: adminTheme.space.md,
            }}
          >
            <label style={labelStyle}>
              Full name
              <input
                style={inputStyle}
                value={form.fullName}
                onChange={handleChange("fullName")}
                placeholder="Enter full name"
                required
              />
            </label>

            <label style={labelStyle}>
              Company email
              <input
                style={inputStyle}
                type="email"
                value={form.companyEmail}
                onChange={handleChange("companyEmail")}
                placeholder="name@hnbstudio.com"
                required
              />
            </label>

            <label style={labelStyle}>
              Mobile number
              <input
                style={inputStyle}
                value={form.mobileNumber}
                onChange={handleChange("mobileNumber")}
                placeholder="Enter mobile number"
                required
              />
            </label>

            <label style={labelStyle}>
              Role
              <select
                style={inputStyle}
                value={form.role}
                onChange={handleChange("role")}
              >
                {roleOptions.map((role) => (
                  <option key={role} value={role}>
                    {role}
                  </option>
                ))}
              </select>
            </label>

            <label style={labelStyle}>
              Department
              <input
                style={inputStyle}
                value={form.department}
                onChange={handleChange("department")}
                placeholder="HR / Finance / Operations"
                required
              />
            </label>

            <label style={labelStyle}>
              Designation
              <input
                style={inputStyle}
                value={form.designation}
                onChange={handleChange("designation")}
                placeholder="HR Manager / Auditor / BDO"
                required
              />
            </label>

            <label style={labelStyle}>
              Reporting to
              <input
                style={inputStyle}
                value={form.reportingTo}
                onChange={handleChange("reportingTo")}
                placeholder="Manager or department head"
              />
            </label>

            <label style={labelStyle}>
              Joining date
              <input
                style={inputStyle}
                type="date"
                value={form.joiningDate}
                onChange={handleChange("joiningDate")}
                required
              />
            </label>

            <label style={labelStyle}>
              Employment type
              <select
                style={inputStyle}
                value={form.employmentType}
                onChange={handleChange("employmentType")}
              >
                <option value="Full-time">Full-time</option>
                <option value="Part-time">Part-time</option>
                <option value="Contract">Contract</option>
              </select>
            </label>

            <label style={labelStyle}>
              Assigned area code
              <input
                style={inputStyle}
                value={form.assignedAreaCode}
                onChange={handleChange("assignedAreaCode")}
                placeholder="Optional"
              />
            </label>

            <label style={labelStyle}>
              Assigned district code
              <input
                style={inputStyle}
                value={form.assignedDistrictCode}
                onChange={handleChange("assignedDistrictCode")}
                placeholder="Optional"
              />
            </label>
          </div>

          <label
            style={{
              ...labelStyle,
              marginTop: adminTheme.space.md,
            }}
          >
            Notes
            <textarea
              style={{
                ...inputStyle,
                minHeight: "110px",
                resize: "vertical",
              }}
              value={form.notes}
              onChange={handleChange("notes")}
              placeholder="Add onboarding notes, document reminders, or provisioning notes"
            />
          </label>

          {error ? (
            <div
              role="alert"
              style={{
                ...alertStyle,
                marginTop: adminTheme.space.md,
                color: "#b42318",
              }}
            >
              {error}
            </div>
          ) : null}

          {createdEmployee ? (
            <div
              style={{
                ...alertStyle,
                marginTop: adminTheme.space.md,
                color: adminTheme.color.text,
              }}
            >
              <strong>Employee created successfully.</strong>
              <p style={{ margin: "8px 0 0" }}>
                {createdEmployee.fullName} ({createdEmployee.companyEmail}) was
                created with staff ID {createdEmployee.staffId}. Temporary
                password: {createdEmployee.defaultPassword}. Must change
                password: {createdEmployee.mustChangePassword ? "Yes" : "No"}.
              </p>
            </div>
          ) : null}

          <div
            style={{
              marginTop: adminTheme.space.lg,
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
              gap: adminTheme.space.md,
            }}
          >
            <div style={secondaryPanelStyle}>
              <strong>Staff ID</strong>
              <p
                style={{ margin: "8px 0 0 0", color: adminTheme.color.textMuted }}
              >
                {generatedPreviewStaffId}
              </p>
            </div>

            <div style={secondaryPanelStyle}>
              <strong>Default password</strong>
              <p
                style={{ margin: "8px 0 0 0", color: adminTheme.color.textMuted }}
              >
                {defaultPassword}
              </p>
            </div>

            <div style={secondaryPanelStyle}>
              <strong>First login policy</strong>
              <p
                style={{ margin: "8px 0 0 0", color: adminTheme.color.textMuted }}
              >
                Must change password immediately after first successful sign-in.
              </p>
            </div>
          </div>

          <div
            style={{
              marginTop: adminTheme.space.lg,
              display: "flex",
              gap: adminTheme.space.sm,
              flexWrap: "wrap",
            }}
          >
            <button
              type="submit"
              style={{
                ...primaryButtonStyle,
                opacity: isSubmitting ? 0.7 : 1,
                cursor: isSubmitting ? "not-allowed" : "pointer",
              }}
              disabled={isSubmitting}
            >
              {isSubmitting ? "Creating employee..." : "Create employee record"}
            </button>
          </div>
        </form>
      </SectionCard>
    </div>
  );
};

export default HrEmployeeOnboardingPage;